import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiPiece, ApiProject } from "@/hooks/use-projects";
import { PipelineStageIndicator } from "@/components/ui/pipeline-stage";
import { PlatformIcon, getPlatformLabel } from "@/components/ui/platform-icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PieceStatusSummary } from "./piece-status-summary";
import { Pencil } from "lucide-react";

interface ProjectHeaderProps {
  project: ApiProject;
  /** When provided, renders a per-status piece-count strip under the stage indicator. */
  pieces?: ApiPiece[];
  /** Gates the inline brief editor (usePermissions().canEdit at the call site). */
  canEditBrief?: boolean;
  onSaveBrief?: (brief: string) => void;
  briefSaving?: boolean;
}

export function ProjectHeader({
  project,
  pieces,
  canEditBrief,
  onSaveBrief,
  briefSaving,
}: ProjectHeaderProps) {
  const { t } = useTranslation("projects");
  const [editingBrief, setEditingBrief] = useState(false);
  const [briefDraft, setBriefDraft] = useState("");

  const startEdit = () => {
    setBriefDraft(project.brief);
    setEditingBrief(true);
  };

  const saveBrief = () => {
    onSaveBrief?.(briefDraft.trim());
    setEditingBrief(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">{project.title}</h1>
        {editingBrief ? (
          <div className="mt-2 space-y-2">
            <Textarea
              value={briefDraft}
              onChange={(e) => setBriefDraft(e.target.value)}
              rows={4}
              autoFocus
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveBrief} disabled={briefSaving || !briefDraft.trim()}>
                {briefSaving ? t("projects:header.saving") : t("common:actions.save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingBrief(false)} disabled={briefSaving}>
                {t("common:actions.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-1 flex items-start gap-2">
            <p className="text-muted-foreground">{project.brief}</p>
            {canEditBrief && onSaveBrief && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 flex-shrink-0 text-xs"
                onClick={startEdit}
              >
                <Pencil size={12} className="mr-1" /> {t("common:actions.edit")}
              </Button>
            )}
          </div>
        )}
      </div>
      <PipelineStageIndicator currentStage={project.pipelineStage} />
      {pieces && <PieceStatusSummary pieces={pieces} />}
      <div className="flex flex-wrap gap-2">
        {project.targetPlatforms.map((p) => (
          <Badge key={p} variant="secondary" className="gap-1">
            <PlatformIcon platform={p} size={12} />
            {getPlatformLabel(p)}
          </Badge>
        ))}
        {project.targetFormats.map((f) => (
          <Badge key={f} variant="outline" className="capitalize">
            {f.toLowerCase()}
          </Badge>
        ))}
      </div>
    </div>
  );
}
